import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { Entity, EntityFilterRequest } from './entities.model';
import { EntitiesService } from './entities.service';
import { helperService } from 'src/app/utils/helper';
import { SavedTableState, TableStateService } from 'src/app/shared/table-state.service';
import { AuthGuard } from 'src/app/account/auth/app.gaurd';
import { Table } from 'primeng/table';

@Component({
    selector: 'app-entities-grid',
    templateUrl: './entities-grid.component.html',
    styleUrls: ['./entities-grid.component.scss'],
    standalone: false
})
export class EntitiesGridComponent implements OnInit, OnDestroy {
  // v13-migration placeholder (Ivy basic template-check requires the symbol to exist; undefined at runtime == v12 behaviour; Phase 2: align template)
  loadTasksData: any;
  @ViewChild('dt') table!: Table;

  entities: Entity[] = [];
  cols: any[] = [];
  loading: boolean = true;
  totalRecords: number = 0;
  first: number = 1;
  searchTerm: string = '';
  entityScreenName = 'entities_grid';
  taskUserId = this.auth?.jwtData?.email;
  public defaultFilters: { [s: string]: any } = {};
  taskInitialLoadDone: boolean = false;

  selectedEntity: Entity | null = null;
  entityDetailDialog: boolean = false;
  viewOnly: boolean = true;

  matchModeOptionsString?: SelectItem[];

  public lastTableLazyLoadEvent: any = null;
  subscriptions: any = null;

  constructor(
    private entitiesService: EntitiesService,
    private filterService: helperService,
    private tableStateService: TableStateService,
    public auth: AuthGuard,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initializeColumns();
    this.setupFilterOptions();
    const saved = this.tableStateService.getState(this.entityScreenName, this.taskUserId);

    if (saved && !this.taskInitialLoadDone) {
      this.restoreTaskState(saved);
    }
  }

  ngOnDestroy(): void {
  }


  ngAfterViewInit() {
    // Capture the table's initial, un-filtered state structure
    // This needs a slight delay to ensure the table component is ready.
    setTimeout(() => {
      if (this.table && this.table.filters) {
        // Deep clone the initial filters to use as a fallback
        this.defaultFilters = JSON.parse(JSON.stringify(this.table.filters));
      }
    });
  }

  private initializeColumns(): void {
    this.cols = [
      { field: 'groupAccountName', header: 'Group Account' },
      { field: 'name', header: 'Entity Name' },
      { field: 'jurisdictionName', header: 'Jurisdiction' },
      { field: 'jurisdictionIdNumber', header: 'Jurisdiction Id' },
      { field: 'associatedProjectIds', header: 'Associated Project IDs' },
      { field: 'associatedTaskIds', header: 'Associated Task IDs' }
    ];
  }

  private setupFilterOptions(): void {
    this.matchModeOptionsString = [
      { label: 'Contains', value: 'contains' },
      { label: 'Starts with', value: 'startsWith' },
      { label: 'Ends with', value: 'endsWith' },
      { label: 'Equals', value: 'equals' }
    ];
  }

  /**
   * Load entities data with lazy loading
   */
  loadEntitiesData(event: any): void {
    this.saveState(
      this.entityScreenName,
      this.taskUserId,
      event,
      {
        searchTerm: this.searchTerm,
      }
    );
    this.loading = true;
    this.lastTableLazyLoadEvent = event;

    const pageNumber = Math.floor(event.first / event.rows) + 1;
    const pageSize = event.rows;

    // // Build filter payload
    // let filterPayload: EntityFilterRequest = {
    //   filters: [],
    //   sort: [],
    //   logic: 'and',
    //   searchTerm: this.searchTerm || '',
    //   fromWidgetFilters: false
    // };

    if (this.subscriptions)
      this.subscriptions.unsubscribe();

    let filterPayload = this.filterService.generateEventPayload(event);

    filterPayload["searchTerm"] = this.searchTerm;

    this.subscriptions = this.entitiesService.getEntities(pageNumber, pageSize, filterPayload)
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.entities = response.data;
            this.entities.forEach(entity => {
              entity["disableEditEntity"] = ((entity.isTkUpdatedEntity != 'Yes' || entity.isTkUpdatedEntity == null)&& entity.subjectEntityId==null)?false:true;
              entity["disableEditEntityTitle"] = entity["disableEditEntity"] ? 'Entity is not editable' : 'Edit entity';
            });
            this.totalRecords = response.customValues?.totalNumberOfRecords || 0;
          } else {
            this.entities = [];
            this.totalRecords = 0;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading entities:', error);
          this.entities = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });


  }

  /**
   * View entity details when entity name is clicked
   */
  viewEntityDetails(entity: Entity): void {
    this.selectedEntity = entity;
    this.entityDetailDialog = true;
    this.viewOnly = true;
  }

  /**
   * Close entity detail dialog
   */
  closeEntityDetailDialog(): void {
    this.entityDetailDialog = false;
    this.selectedEntity = null;
    if(!this.viewOnly){
      this.viewOnly = true;
      this.loadEntitiesData(this.lastTableLazyLoadEvent);
    }
  }
  /**
   * Clear all filters
   */
  clearFilters(table: any): void {
    this.searchTerm = '';
    table.clear();
  }

  /**
   * Navigate to project with filter
   */
  navigateToProject(projectId: number): void {
    console.log('Navigating to project with ID:', projectId);
    const filters = [
      {
        field: 'id',
        operator: 'equals',
        value: projectId.toString()
      }
    ];

    console.log('Filters:', filters);

    // Navigate to projects page with state
    this.router.navigate(['/projects'], {
      state: {
        filters: filters,
        fromEntity: true
      }
    });
  }

  /**
   * Navigate to task with filter
   */
  navigateToTask(taskId: number): void {
    const filters = [
      {
        field: 'id',
        operator: 'equals',
        value: taskId.toString()
      }
    ];

    // Navigate to task-grid page with state
    this.router.navigate(['/task-grid'], {
      state: {
        filters: filters,
        fromEntity: true
      }
    });
  }

  restoreTaskState(saved: any) {
    const event = saved.event;
    const widgetState = history.state;

    if (widgetState && widgetState.filters && (widgetState.fromWidget || widgetState.fromEntity)) {
      return; // Skip saving state if navigated from widget or entity with filters
    }

    this.searchTerm = saved.customFilters.searchTerm;

    setTimeout(() => {
      if (this.table) {

        const filtersAreValid = event.filters && Object.keys(event.filters).length > 0;

        // 2. Assign filters: Use saved filters if valid, otherwise use the captured default filters.
        if (filtersAreValid) {
          this.table.filters = event.filters;
        } else if (Object.keys(this.defaultFilters).length > 0) {
          // 🔥 Use the default structure as a fallback to prevent empty filter popups
          this.table.filters = this.defaultFilters;
        }

        // Restore sorting
        this.table.sortField = event.sortField ?? '';
        this.table.sortOrder = event.sortOrder ?? 0;

        // 🔥 force PrimeNG to show sorting icons
        this.table.sortSingle();

        // Restore paginator
        this.table.first = event.first ?? 0;
        this.table.rows = event.rows ?? 10;

        this.table.onLazyLoad.emit(event);

      }
    }, 50);
    this.taskInitialLoadDone = true;
  }

  saveState(screen: string, userId: string, event: LazyLoadEvent, customFilters: any) {
    const widgetState = history.state;

    if (widgetState && widgetState.filters && (widgetState.fromWidget || widgetState.fromEntity)) {
      return; // Skip saving state if navigated from widget or entity with filters
    }
    const state: SavedTableState = {
      event: {
        first: event.first,
        rows: event.rows,
        sortField: event.sortField,
        sortOrder: event.sortOrder,
        filters: event.filters
      },
      customFilters: { ...customFilters }
    };

    this.tableStateService.saveState(screen, userId, state);
  }

  openEntityDialog(): void {
    this.entityDetailDialog = true;
    this.selectedEntity = null; 
    this.viewOnly = false;
  }

  editEntity(entity: Entity): void {
    this.selectedEntity=entity;
    this.entityDetailDialog = true;
    this.viewOnly = false;
  }

}